import { Dispatch, SetStateAction } from "react";

import moment from "moment";

import { NotificationManagerAPI } from "notifications/NotificationManager";
import { parseSampledDiscoveries } from "reports/ReportDiscoveriesManager";
import { parseSampledGoals } from "reports/ReportGoalsManager";
import { calculateTrajectories } from "reports/TrajectoryCalculator";
import { calculateReturnToWorkStatus, calculateMedicationReductionStatus } from "reports/ProgramImpactCalculator";

import * as M from "reports/measures/Measure";
import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as R from "reports/Report";
import * as RD from "reports/ReportDef";
import * as RDPD from "reports/ReportDataProcessorDef";
import * as T from "reports/measures/Trajectory";

// ---=== Feature Flags ===--- //

/**
 * Enable/disable display of citations throughout the report.
 * When false, hides:
 * - Citation tooltips on trajectory measures
 * - CitationDisplay components inline with text
 * - Citations section divider and dropdown
 *
 * Note: Citation parsing logic remains active regardless of this flag.
 */
export const ENABLE_CITATIONS_DISPLAY = true;

// ---=== Utility Functions ===--- //

/**
 * Normalizes discoveries to the new format (array of objects with text and citation properties).
 * Handles both old format (array of strings) and new format (array of objects).
 * @param discoveries - Raw discoveries data that may be in old or new format
 * @returns Normalized discoveries in the new format
 */
const normalizeDiscoveries = (discoveries: RD.Discoveries): RD.Discoveries => {
    if (!Array.isArray(discoveries)) {
        return [];
    }

    return discoveries.map(discovery => {
        if (typeof discovery === 'string') {
            // Old format: convert string to object
            return { text: discovery, citation: undefined };
        } else if (discovery && typeof discovery === 'object' && 'text' in discovery) {
            // New format: already an object with text property
            return discovery;
        } else {
            // Fallback: convert to string then to object
            return { text: String(discovery || ''), citation: undefined };
        }
    });
};

interface processCoachingPlanParams {
    isGenerator: boolean;
    notificationManager: NotificationManagerAPI,
    report: RD.EditableReport;
    // State Setters
    setHasCoachingPlan: Dispatch<SetStateAction<boolean>>;
    setProgramType: Dispatch<SetStateAction<string>>;
}

const processCoachingPlan = ({
    isGenerator,
    notificationManager,
    report,
    // State Setters
    setHasCoachingPlan,
    setProgramType
}: processCoachingPlanParams): boolean => {

    const sampleData: RD.ReportSampleData = report.sample_data;

    // @todo Move these checks to the KarunaCLOUD OutcomeReportSerializer?

    if (isGenerator) {
        if (sampleData.coaching_plan === "None") {
            notificationManager.addError("Unable to create Outcome Report! Patient must have an active or previously completed Coaching Plan.");
            return false
        }
        if (sampleData.current_coaching_plan_week <= 0) {
            notificationManager.addError("Unable to create Outcome Report! Patient must have an active or previously completed Coaching Plan at Week 1 later.");
            return false;
        }
        if (sampleData.program_type === "No Active Coaching Plan") {
            notificationManager.addError("Unable to create Outcome Report! The report data does not contain a program type. Contact a Karuna Admin as this should not be possible.");
            return false;
        }
        if (sampleData.coaching_plan === "Most Recently Completed") {
            notificationManager.addError("Patient has no active coaching plan. This Outcome Report will use data from their most recently completed coaching plan.");
        }
    }

    setProgramType(sampleData.program_type);
    setHasCoachingPlan(true);
    return true;
};

interface processReportConfigurationParams {
    isGenerator: boolean;
    report: RD.EditableReport;
    // State Setters
    setIsReportConfigurationDirty: Dispatch<SetStateAction<boolean>>;
    setPreparedDate: Dispatch<SetStateAction<Date>>;
    setPreparerName: Dispatch<SetStateAction<string>>;
    setReportPlanWeeks: Dispatch<SetStateAction<number>>;
    setReportWeek: Dispatch<SetStateAction<RD.ReportWeek>>;
    setReportWeekOptions: Dispatch<SetStateAction<RD.ReportWeekOptions>>;
    setSamplePlanWeeks: Dispatch<SetStateAction<number>>;
}

const processReportConfiguration = ({
    isGenerator,
    report,
    // State Setters
    setIsReportConfigurationDirty,
    setPreparedDate,
    setPreparerName,
    setReportPlanWeeks,
    setReportWeek,
    setReportWeekOptions,
    setSamplePlanWeeks
}: processReportConfigurationParams): [RD.ReportWeek, number] => {

    const sampleData: RD.ReportSampleData = report.sample_data;
    const generatedData: RD.ReportGeneratedData = report.generated_data;

    setPreparedDate(new Date(report.prepared_date));
    setPreparerName(report.preparer_name);

    // If reportWeek or reportPlanWeeks already exist in previously saved generated
    // report data, set them here so they are marked clean when report processing is
    // complete. This will prevent Auto-Save from sending a patch update to the backend
    // to save them.
    let reportWeek: RD.ReportWeek = generatedData?.reportWeek;
    let reportPlanWeeks: number = generatedData?.reportPlanWeeks;

    if (!reportWeek && !reportPlanWeeks) {
        if (isGenerator) {
            setIsReportConfigurationDirty(true);
        }
        else {
            // @todo Call notificationManager.addError with an error. Both reportWeeks and reportPlanWeeks should have been included in the generated data!
            console.error("Generated report data is missing reportWeek or reportPlanWeeks. This should not happen.");
        }
    }

    if (isGenerator) {
        // Only generator mode needs samplePlanWeeks for default reportWeek
        // configuration, and when changing plan weeks using the report week
        // selection dropdown.
        const samplePlanWeeks = Number(sampleData.sample_plan_weeks)
        setSamplePlanWeeks(samplePlanWeeks);

        // Only generator mode needs the report week dropdown.
        // Limit options to current coaching plan week or earlier to prevent creating reports for future weeks
        const currentPlanWeek = sampleData.current_coaching_plan_week;
        const maxSelectableWeek = Math.min(samplePlanWeeks, currentPlanWeek);

        // Use reportWeek and reportPlanWeeks from previously saved generated data
        // if they exist, otherwise set defaults using sample data input.
        // Default to current plan week (or Month 6 if post-program checkins exist)
        reportWeek = reportWeek ?? (sampleData.sample_has_post_program_checkins ? MD.Month6Token : currentPlanWeek) as RD.ReportWeek;
        reportPlanWeeks = reportPlanWeeks ?? samplePlanWeeks;

        let options: RD.ReportWeekOptions = [];
        for (let planWeek = 1; planWeek <= maxSelectableWeek; planWeek++) {
            options.push({ label: `Week ${planWeek}`, value: planWeek });
        }
        // Add Month 6 option if post-program checkins exist
        if (sampleData.sample_has_post_program_checkins) {
            options.push({ label: "6 Month Report", value: MD.Month6Token });
        }
        setReportWeekOptions(options);
    }

    setReportWeek(reportWeek);
    setReportPlanWeeks(reportPlanWeeks);

    return [reportWeek, reportPlanWeeks];
};

interface processPatientInfoParams {
    report: RD.EditableReport;
    // State Setters
    setBrand: Dispatch<SetStateAction<RD.BrandEnums>>;
    setClaimNumber: Dispatch<SetStateAction<string>>;
    setCoach: Dispatch<SetStateAction<string>>;
    setDateOfBirth: Dispatch<SetStateAction<string>>;
    setDateOfInjury: Dispatch<SetStateAction<string>>;
    setHasPhysicianOversight: Dispatch<SetStateAction<boolean>>;
    setPatientId: Dispatch<SetStateAction<number>>;
    setPatientName: Dispatch<SetStateAction<string>>;
    setPatientNameMasked: Dispatch<SetStateAction<string>>;
    setPayerType: Dispatch<SetStateAction<RD.PayerEnums>>;
    setPhysician: Dispatch<SetStateAction<string>>;
}

const processPatientInfo = ({
    report,
    // State Setters
    setBrand,
    setClaimNumber,
    setCoach,
    setDateOfBirth,
    setDateOfInjury,
    setHasPhysicianOversight,
    setPatientId,
    setPatientName,
    setPatientNameMasked,
    setPayerType,
    setPhysician
}: processPatientInfoParams): void => {

    const sampleData: RD.ReportSampleData = report.sample_data;

    const patientId = sampleData.patient_id;
    setPatientId(patientId);

    const payerType = (sampleData.payer_type !== undefined && RD.PayerEnums[sampleData.payer_type] !== undefined)
        ? sampleData.payer_type
        : RD.PayerEnums.KarunaHome;
    setPayerType(payerType);
    const brand = (sampleData.brand !== undefined && RD.BrandEnums[sampleData.brand] !== undefined)
        ? sampleData.brand
        : RD.BrandEnums.KarunaHome;
    setBrand(brand);

    if (sampleData.patient_name) {
        console.log('[ReportDataProcessor] Sample data from backend:', {
            patient_name: sampleData.patient_name,
            patient_name_masked: sampleData.patient_name_masked,
            payer_type: sampleData.payer_type
        });
        setPatientName(sampleData.patient_name);
        setPatientNameMasked(sampleData.patient_name_masked);
    }

    if (sampleData.dob) setDateOfBirth(sampleData.dob);
    if (sampleData.claim_number) setClaimNumber(sampleData.claim_number);
    if (sampleData.date_of_injury) setDateOfInjury(sampleData.date_of_injury);
    if (sampleData.coach) setCoach(sampleData.coach);
    if (sampleData.physician) setPhysician(sampleData.physician);
    if (sampleData.has_physician_oversight_agreement !== undefined) {
        setHasPhysicianOversight(sampleData.has_physician_oversight_agreement);
    }
};

interface processHistoryOfInjuryParams {
    isGenerator: boolean;
    report: RD.EditableReport;
    reportWeek: RD.ReportWeek,
    // State Setters
    setHistoryOfInjury: Dispatch<SetStateAction<string>>;
    setHistoryOfInjuryOriginal: Dispatch<SetStateAction<string>>;
}

const processHistoryOfInjury = ({
    isGenerator,
    report,
    reportWeek,
    // State Setters
    setHistoryOfInjury,
    setHistoryOfInjuryOriginal
}: processHistoryOfInjuryParams): number | undefined => {

    const sampleData: RD.ReportSampleData = report.sample_data;
    const generatedData: RD.ReportGeneratedData = report.generated_data;

    let historyStr = "";
    let citationIndex: number | undefined = undefined;

    if (sampleData.condition_history) {
        let history: { week: MD.MeasureWeek; value: string; citation?: number }[] = [];
        let histories = Object.entries(sampleData.condition_history);
        histories.forEach(([weekStr, historyData]: [string, any]) => {
            let measureWeek: MD.MeasureWeek = M.getMeasureWeek(weekStr);
            if (R.includeMeasureWeekInReport(reportWeek, measureWeek)) {
                let items: string[];
                let weekCitation: number | undefined;

                // Check if new format (object with items and citation) or old format (array of strings)
                if (typeof historyData === 'object' && historyData !== null && 'items' in historyData) {
                    // New format: { items: string[], citation?: number }
                    items = Array.isArray(historyData.items) ? historyData.items : [];
                    weekCitation = historyData.citation;
                } else if (Array.isArray(historyData)) {
                    // Old format: string[]
                    items = historyData;
                    weekCitation = undefined;
                } else {
                    // Fallback
                    items = [];
                    weekCitation = undefined;
                }

                if (items.length > 0) {
                    let text = items.map(item => {
                        const trimmed = String(item).trim();
                        return trimmed + (trimmed.endsWith(".") ? "" : ".");
                    }).join("\r\n");

                    history.push({ week: measureWeek, value: text, citation: weekCitation });
                }
            }
        });
        if (history.length > 0) {
            const lastHistory = history[history.length - 1];
            historyStr = lastHistory.value;
            // Only collect citation from the most recent history item
            if (lastHistory.citation !== undefined && lastHistory.citation !== null) {
                citationIndex = lastHistory.citation;
            }
        }
    }

    const generatedHistory = generatedData?.historyOfInjury;
    if (generatedHistory) {
        setHistoryOfInjury(generatedHistory);
        setHistoryOfInjuryOriginal(generatedHistory)
    }
    else if (isGenerator && historyStr) {
        setHistoryOfInjury(historyStr);
    }

    return citationIndex;
};

interface processSubjectiveOutcomesParams {
    isGenerator: boolean;
    report: RD.EditableReport;
    reportWeek: RD.ReportWeek;
    // State Setters
    setDiscoveries: Dispatch<SetStateAction<RD.Discoveries | null>>;
    setDiscoveriesOriginal: Dispatch<SetStateAction<RD.Discoveries | null>>;
    setEngagementLevel: Dispatch<SetStateAction<RD.EngagementLevel |  null>>;
    setGoalsProgress: Dispatch<SetStateAction<RD.GoalsProgress | null>>;
    setGoals: Dispatch<SetStateAction<RD.Goals | null>>;
    setGoalsOriginal: Dispatch<SetStateAction<RD.Goals | null>>;
    setIncludeEngagementLevel: Dispatch<SetStateAction<boolean>>;
    setIncludeGoalsProgress: Dispatch<SetStateAction<boolean>>;
    setSubjectiveProgressSummary: Dispatch<SetStateAction<string>>;
    setSubjectiveProgressSummaryOriginal: Dispatch<SetStateAction<string>>;
    setSubjectiveProgressSummaryCitation: Dispatch<SetStateAction<number | undefined>>;
}

const processSubjectiveOutcomes = ({
    isGenerator,
    report,
    reportWeek,
    // State Setters
    setDiscoveries,
    setDiscoveriesOriginal,
    setEngagementLevel,
    setGoalsProgress,
    setGoals,
    setGoalsOriginal,
    setIncludeEngagementLevel,
    setIncludeGoalsProgress,
    setSubjectiveProgressSummary,
    setSubjectiveProgressSummaryOriginal,
    setSubjectiveProgressSummaryCitation
}: processSubjectiveOutcomesParams): void => {

    const sampleData: RD.ReportSampleData = report.sample_data;
    const generatedData: RD.ReportGeneratedData = report.generated_data;

    const subjectiveOutcomes = sampleData.subjective_outcomes;
    if (subjectiveOutcomes) {
        let discoveriesHistory: RDPD.DiscoveriesDetails = [];
        let engagementLevelHistory: RDPD.EngagementLevelDetails = [];
        let goalsHistory: RDPD.GoalsDetails = [];
        let goalsProgressHistory: RDPD.GoalsProgressDetails = [];
        let subjectiveProgressSummaryHistory: RDPD.SubjectiveProgressSummaryDetails = [];

        if (isGenerator) {
            Object.entries(subjectiveOutcomes).forEach(
                ([weekStr, weekData]: [string, RDPD.SubjectiveOutcomeWeekData]) => {
                    let measureWeek: MD.MeasureWeek = M.getMeasureWeek(weekStr);
                    if (R.includeMeasureWeekInReport(reportWeek, measureWeek)) {
                        if (weekData.discoveries !== undefined) {
                            const current: RD.Discoveries = parseSampledDiscoveries(weekData.discoveries);
                            discoveriesHistory.push({ week: measureWeek, value: current });
                        }
                        if (weekData.level_of_engagement_in_program !== undefined && weekData.level_of_engagement_in_program !== null) {
                            // Support both legacy (string/number) and new format (object with value and citation)
                            const engagementData = weekData.level_of_engagement_in_program;
                            let current: number;
                            let citation: number | undefined;

                            if (engagementData !== null && typeof engagementData === 'object' && 'value' in (engagementData as any)) {
                                // New format: { value: number, citation?: number }
                                current = Number((engagementData as any).value);
                                citation = (engagementData as any).citation;
                            } else {
                                // Legacy format: a plain number/string (no citation)
                                current = Number(engagementData);
                                citation = undefined;
                            }

                            // Only add to history if current is a valid number (not null/undefined converted to NaN)
                            if (!isNaN(current) && current !== null) {
                                engagementLevelHistory.push({ week: measureWeek, value: { week: measureWeek, value: current, citation } });
                            }
                        }
                        if (weekData.program_goals?.goals !== undefined) {
                            // Support both legacy format (citations array) and new format (single citation field)
                            const citations = (weekData.program_goals as any).citation !== undefined
                                ? (weekData.program_goals as any).citation
                                : weekData.program_goals?.citations;
                            const current: RD.Goals = parseSampledGoals(weekData.program_goals?.goals, citations);
                            goalsHistory.push({ week: measureWeek, value: current });
                        }
                        if (weekData.perceived_progress_towards_goals !== undefined && weekData.perceived_progress_towards_goals !== null) {
                            // Support both legacy (string/number) and new format (object with value and citation)
                            const progressData = weekData.perceived_progress_towards_goals;
                            let current: number;
                            let citation: number | undefined;

                            if (progressData !== null && typeof progressData === 'object' && 'value' in (progressData as any)) {
                                // New format: { value: number, citation?: number }
                                current = Number((progressData as any).value);
                                citation = (progressData as any).citation;
                            } else {
                                // Legacy format: a plain number/string (no citation)
                                current = Number(progressData);
                                citation = undefined;
                            }

                            // Only add to history if current is a valid number (not null/undefined converted to NaN)
                            if (!isNaN(current) && current !== null) {
                                goalsProgressHistory.push({ week: measureWeek, value: { week: measureWeek, value: current, citation } });
                            }
                        }
                        if (weekData.overall_subjective_progress !== undefined) {
                            // Support both legacy (array) and new format (object with an items array and citation)
                            let items: string[];
                            let citation: number | undefined;

                            if (typeof weekData.overall_subjective_progress === 'object' && weekData.overall_subjective_progress !== null && 'items' in weekData.overall_subjective_progress) {
                                // New format: { items: string[], citation?: number }
                                const progressData = weekData.overall_subjective_progress as any;
                                const progressItems = progressData.items;
                                items = Array.isArray(progressItems) ? progressItems : [];
                                citation = progressData.citation;
                            } else if (Array.isArray(weekData.overall_subjective_progress)) {
                                // Legacy format: plain array
                                items = weekData.overall_subjective_progress;
                                citation = undefined;
                            } else {
                                // Fallback: convert to string
                                items = [String(weekData.overall_subjective_progress)];
                                citation = undefined;
                            }

                            const current: RD.SubjectiveProgressSummary = items.join("\n\n");
                            subjectiveProgressSummaryHistory.push({ week: measureWeek, value: current, citation } as any);
                        }
                    }
                }
            );
        }

        // Use the latest available recordings from the sample report data for each of these items.

        let mostRecentDiscoveries: RD.Discoveries;
        let mostRecentEngagementLevel: RD.EngagementLevel;
        let mostRecentGoals: RD.Goals;
        let mostRecentGoalsProgress: RD.GoalsProgress;
        let mostRecentSubjectiveProgressSummary: RD.SubjectiveProgressSummary;

        if (isGenerator) {
            // Normalize discoveries from generated_data to ensure they're in the new format
            const rawDiscoveries = generatedData?.discoveries ?? M.getMostRecentMeasureWeekRecord<RD.Discoveries>(discoveriesHistory);
            mostRecentDiscoveries = rawDiscoveries ? normalizeDiscoveries(rawDiscoveries) : rawDiscoveries;
            mostRecentEngagementLevel = generatedData?.engagementLevel ?? M.getMostRecentMeasureWeekRecord<RD.EngagementLevel>(engagementLevelHistory);
            mostRecentGoals = generatedData?.goals ?? M.getMostRecentMeasureWeekRecord<RD.Goals>(goalsHistory);
            mostRecentGoalsProgress = generatedData?.goalsProgress ?? M.getMostRecentMeasureWeekRecord<RD.GoalsProgress>(goalsProgressHistory);
            mostRecentSubjectiveProgressSummary = generatedData?.subjectiveProgressSummary ?? M.getMostRecentMeasureWeekRecord<RD.SubjectiveProgressSummary>(subjectiveProgressSummaryHistory);
        }
        else {
            // Normalize discoveries from generated_data to ensure they're in the new format
            const rawDiscoveries = generatedData?.discoveries ?? null;
            mostRecentDiscoveries = rawDiscoveries ? normalizeDiscoveries(rawDiscoveries) : rawDiscoveries;
            mostRecentEngagementLevel = generatedData?.engagementLevel ?? null;
            mostRecentGoals = generatedData?.goals ?? null;
            mostRecentGoalsProgress = generatedData?.goalsProgress ?? null;
            mostRecentSubjectiveProgressSummary = generatedData?.subjectiveProgressSummary ?? null;
        }

        if (mostRecentDiscoveries) {
            setDiscoveries(mostRecentDiscoveries);
            setDiscoveriesOriginal(mostRecentDiscoveries);
        }

        if (mostRecentEngagementLevel) {
            setEngagementLevel(mostRecentEngagementLevel);
            setIncludeEngagementLevel(generatedData?.inclusions?.engagementLevel ?? true);
        }

        if (mostRecentGoals) {
            setGoals(mostRecentGoals);
            setGoalsOriginal(mostRecentGoals);
        }

        if (mostRecentGoalsProgress) {
            setGoalsProgress(mostRecentGoalsProgress);
            setIncludeGoalsProgress(generatedData?.inclusions?.goalsProgress ?? true);
        }

        if (mostRecentSubjectiveProgressSummary) {
            setSubjectiveProgressSummary(mostRecentSubjectiveProgressSummary);
            if (!isGenerator) {
                setSubjectiveProgressSummaryOriginal(mostRecentSubjectiveProgressSummary);
            }

            // Extract citation from the most recent subjective progress summary record
            if (isGenerator && subjectiveProgressSummaryHistory.length > 0) {
                const mostRecentRecord = subjectiveProgressSummaryHistory[subjectiveProgressSummaryHistory.length - 1];
                if ((mostRecentRecord as any).citation !== undefined) {
                    setSubjectiveProgressSummaryCitation((mostRecentRecord as any).citation);
                }
            }
        }
    }
};

interface processObjectiveOutcomesParams {
    isGenerator: boolean;
    report: RD.EditableReport;
    reportPlanWeeks: number;
    reportWeek: RD.ReportWeek;
    // State Setters
    setAllTrajectories: Dispatch<SetStateAction<T.Trajectories>>;
    setAverageActivityTimePerWeek: Dispatch<SetStateAction<string>>;
    setIncludeAverageTime: Dispatch<SetStateAction<boolean>>;
    setIncludeMedicationReduction: Dispatch<SetStateAction<boolean>>;
    setIncludeReturnToWork: Dispatch<SetStateAction<boolean>>;
    setIncludeSessionsAttended: Dispatch<SetStateAction<boolean>>;
    setIncludeUniqueDays: Dispatch<SetStateAction<boolean>>;
    setIncludedTrajectories: Dispatch<SetStateAction<Record<string, boolean>>>;
    setMedicationReductionStatus: Dispatch<SetStateAction<RD.MedicationReductionStatus | null>>;
    setReturnToWorkStatus: Dispatch<SetStateAction<RD.ReturnToWorkStatus | null>>;
    setSelectedImprovedTrajectoryTypes: Dispatch<SetStateAction<MD.MeasureType[]>>;
    setSessionsAttended: Dispatch<SetStateAction<number>>;
    setUniqueDays: Dispatch<SetStateAction<number>>
}

const processObjectiveOutcomes = ({
    isGenerator,
    report,
    reportWeek,
    reportPlanWeeks,
    // State Setters
    setAllTrajectories,
    setAverageActivityTimePerWeek,
    setIncludeAverageTime,
    setIncludeMedicationReduction,
    setIncludeReturnToWork,
    setIncludeSessionsAttended,
    setIncludeUniqueDays,
    setIncludedTrajectories,
    setMedicationReductionStatus,
    setReturnToWorkStatus,
    setSelectedImprovedTrajectoryTypes,
    setSessionsAttended,
    setUniqueDays
}: processObjectiveOutcomesParams): void => {

    const sampleData: RD.ReportSampleData = report.sample_data;
    const generatedData: RD.ReportGeneratedData = report.generated_data;

    const objectiveOutcomes = sampleData.objective_outcomes;
    if (objectiveOutcomes) {
        const adherence = objectiveOutcomes?.adherence;

        // ---=== Adherence ===--- //

        let averageActivityTimePerWeek = "N/A";
        let sessions = 0;
        let uniqueDays = 0.0;

        let includeAvgTime = generatedData?.inclusions?.averageTime ?? true;
        let includeSessions = generatedData?.inclusions?.sessionsAttended ?? true;
        let includeUnique = generatedData?.inclusions?.uniqueDays ?? true;

        if (adherence) {
            const vetSeconds = adherence.vet_seconds_by_week ?? {};
            if (Object.keys(vetSeconds).length > 0) {
                let sum = 0;
                for (let week = 1; week <= reportPlanWeeks; week++) {
                    const weekKey = week.toString();
                    if (vetSeconds[weekKey] !== undefined) {
                        sum += vetSeconds[weekKey] ?? 0;
                    }
                }
                const avg = (reportPlanWeeks > 0) ? (sum / reportPlanWeeks) : 0;
                let duration = moment.duration(avg, "seconds");
                let hours = duration.hours() + duration.days() * 24;
                let minutes = duration.minutes();
                let seconds = duration.seconds();
                averageActivityTimePerWeek = `${hours > 0 ? hours + "h " : ""}${minutes ? minutes + "m " : ""}${seconds > 0 ? seconds + "s" : ""}`.trim() || "0s";
            }

            // Limit sessions_attended to the report week
            // E.g., if patient has 12 sessions but coach is creating a Week 4 report, clamp to 4
            const rawSessions = adherence.sessions_attended ?? 0;
            sessions = R.isMonth6Report(reportWeek) ? rawSessions : Math.min(rawSessions, reportWeek);
            const uniqueDaysData = adherence.unique_day_vet_usage_by_week ?? {};
            if (Object.keys(uniqueDaysData).length > 0) {
                let sum = 0;
                for (let week = 1; week <= reportPlanWeeks; week++) {
                    const weekKey = week.toString();
                    if (uniqueDaysData[weekKey] !== undefined) {
                        sum += uniqueDaysData[weekKey] ?? 0;
                    }
                }
                uniqueDays = (reportPlanWeeks > 0) ? parseFloat((sum / reportPlanWeeks).toFixed(1)) : 0.0;
            }
        }

        if (averageActivityTimePerWeek !== "N/A") setAverageActivityTimePerWeek(averageActivityTimePerWeek);
        setIncludeAverageTime(includeAvgTime);
        if (sessions !== 0) setSessionsAttended(sessions);
        setIncludeSessionsAttended(includeSessions);
        if (uniqueDays !== 0.0) setUniqueDays(uniqueDays);
        setIncludeUniqueDays(includeUnique);

        // ---=== Process Trajectories ===--- //

        // Use shared trajectory calculator to ensure consistent logic
        const allTrajectories = calculateTrajectories(objectiveOutcomes, reportWeek);

        // ---=== Populate Medication and Work Measures for Program Impact ===--- //

        let medicationMeasures: MD.Measures = [];
        let workMeasures: MD.Measures = [];

        const measuresMap: { [key in MD.MeasureType]?: MD.Measures } = {
            [MD.MeasureTypesMap.Medication]: medicationMeasures,
            [MD.MeasureTypesMap.Work]: workMeasures
        };

        const sampleLabelToMeasureDetailsMap = new Map<string, { type: MD.MeasureType; props: MP.MeasureProperties }>();
        Object.entries(MP.MeasurePropertiesMap).forEach(([typeKey, props]) => {
            const measureType = MD.MeasureTypesMap[typeKey as keyof typeof MD.MeasureTypesMap];
            const measureProps = props as MP.MeasureProperties;
            if (measureType && measureProps) {
                const sampleLabel = MP.MeasureTypeToSampleLabelMap[measureType];
                if (sampleLabel) {
                    sampleLabelToMeasureDetailsMap.set(sampleLabel, { type: measureType, props: measureProps });
                }
            }
        });

        const otherMeasurements = objectiveOutcomes?.measurements;
        if (otherMeasurements) {
            Object.entries(otherMeasurements).forEach(
                ([weekStr, weekItems]: [string, RDPD.MeasurementItem[]]) => {
                    let measureWeek: MD.MeasureWeek = M.getMeasureWeek(weekStr);
                    weekItems.forEach((item: RDPD.MeasurementItem) => {
                        const measureDetails = sampleLabelToMeasureDetailsMap.get(item.label);
                        if (measureDetails) {
                            const { type } = measureDetails;
                            const targetMeasuresArray = measuresMap[type];
                            if (targetMeasuresArray) {
                                let numericValue = parseFloat(String(item.value));
                                if (!isNaN(numericValue)) {
                                    const measure: any = { type: type, week: measureWeek, value: numericValue };
                                    // Support new format with citation field
                                    if (item.citation !== undefined && item.citation !== null) {
                                        measure.citation = item.citation;
                                    }
                                    targetMeasuresArray.push(measure);
                                }
                            }
                        }
                    });
                }
            );
        }

        const filterMeasuresForReport = (measures: MD.Measures): MD.Measures => {
            if (!measures || measures.length === 0) return [];
            const lib = M.lowerIsBetter(M.type(measures));
            const bestMeasurePerWeek: { [weekKey: string]: MD.Measure } = {};
            for (const currentMeasure of measures) {
                const measureWeek: MD.MeasureWeek = currentMeasure.week;
                if (R.includeMeasureWeekInReport(reportWeek, measureWeek)) {
                    const measureWeekKey: string = measureWeek.toString();
                    const existingBest = bestMeasurePerWeek[measureWeekKey];
                    if (!existingBest) {
                        bestMeasurePerWeek[measureWeekKey] = currentMeasure;
                    } else {
                        const isCurrentBetter = lib ? currentMeasure.value < existingBest.value : currentMeasure.value > existingBest.value;
                        if (isCurrentBetter) bestMeasurePerWeek[measureWeekKey] = currentMeasure;
                    }
                }
            }
            const bestMeasuresArray = Object.values(bestMeasurePerWeek);
            const weekSorter = (a: MD.Measure, b: MD.Measure): number => {
                const aIsMonth6 = a.week === MD.Month6Token;
                const bIsMonth6 = b.week === MD.Month6Token;
                if (aIsMonth6 && bIsMonth6) return 0;
                if (aIsMonth6) return 1;
                if (bIsMonth6) return -1;
                return (a.week as number) - (b.week as number);
            };
            return bestMeasuresArray.sort(weekSorter);
        };

        medicationMeasures = filterMeasuresForReport(medicationMeasures);
        workMeasures = filterMeasuresForReport(workMeasures);

        // ---=== Program Impact ===--- //

        // Use shared program impact calculator for consistent logic
        const returnToWorkStatus = calculateReturnToWorkStatus(workMeasures);
        if (returnToWorkStatus) {
            setReturnToWorkStatus(returnToWorkStatus);
            setIncludeReturnToWork(generatedData?.inclusions?.returnToWork ?? true);
        }

        const medicationReductionStatus = calculateMedicationReductionStatus(medicationMeasures);
        if (medicationReductionStatus) {
            setMedicationReductionStatus(medicationReductionStatus);
            setIncludeMedicationReduction(generatedData?.inclusions?.medicationReduction ?? true);
        }

        // Trajectories are already sorted by the shared calculator
        setAllTrajectories(allTrajectories);

        // ---=== Key Improvement & Trajectory Inclusions ===--- //

        let selectedTypes: MD.MeasureType[];
        let includedTrajectories: Record<string, boolean>;

        if (isGenerator) {
            selectedTypes = allTrajectories
                .filter(ind => ind.bandSeverityTrajectory === T.TrajectoryType.IMPROVED)
                .slice(0, 5)
                .map(ind => ind.measureType);

            const reducedTrajectories = Object.values(MD.MeasureTypesMap).reduce(
                (acc, measureType) => {
                    const trajectory = T.getTrajectoryOfType(measureType as MD.MeasureType, allTrajectories);
                    acc[measureType as string] = !!(trajectory && trajectory.measures.length > 0);
                    return acc;
                }, {} as Record<string, boolean>
            );

            if (Object.keys(reducedTrajectories).length > 0) {
                includedTrajectories = reducedTrajectories;
            }
        }

        const genSelectedTypes = generatedData?.inclusions?.selectedImprovedTrajectoryTypes;
        if (genSelectedTypes) {
            setSelectedImprovedTrajectoryTypes(genSelectedTypes);
        }
        else if (isGenerator && selectedTypes?.length > 0) {
            setSelectedImprovedTrajectoryTypes(selectedTypes);
        }

        const genIncludedTrajectories = generatedData?.inclusions?.trajectories;
        if (genIncludedTrajectories) {
            setIncludedTrajectories(genIncludedTrajectories);
        }
        else if (isGenerator && includedTrajectories) {
            setIncludedTrajectories(includedTrajectories);
        }
    }
};

interface processAssessmentsParams {
    isGenerator: boolean;
    report: RD.EditableReport;
    reportWeek: RD.ReportWeek;
    // State Setters
    setAssessmentsGenerated: Dispatch<SetStateAction<boolean>>;
    setBehavioralAssessments: Dispatch<SetStateAction<string>>;
    setContinuedNeedsAssessments: Dispatch<SetStateAction<string>>;
    setIncludeBehavioralAssessments: Dispatch<SetStateAction<boolean>>;
    setIncludeContinuedNeedsAssessments: Dispatch<SetStateAction<boolean>>;
    setIncludePhysicalAssessments: Dispatch<SetStateAction<boolean>>;
    setIncludeSocialAssessments: Dispatch<SetStateAction<boolean>>;
    setPhysicalAssessments: Dispatch<SetStateAction<string>>;
    setSocialAssessments: Dispatch<SetStateAction<string>>;
    setSocialAssessmentsOriginal: Dispatch<SetStateAction<string>>
}

const processAssessments = ({
    isGenerator,
    report,
    reportWeek,
    // State Setters
    setAssessmentsGenerated,
    setBehavioralAssessments,
    setContinuedNeedsAssessments,
    setIncludeBehavioralAssessments,
    setIncludeContinuedNeedsAssessments,
    setIncludePhysicalAssessments,
    setIncludeSocialAssessments,
    setPhysicalAssessments,
    setSocialAssessments,
    setSocialAssessmentsOriginal
}: processAssessmentsParams): void => {

    const generatedData: RD.ReportGeneratedData = report.generated_data;

    let mostRecentSocialAssessment: string = null;

    if (isGenerator) {
        const sampleData: RD.ReportSampleData = report.sample_data;
        const assessmentsData = sampleData.assessments;

        if (assessmentsData) {
            let socialAssessmentsHistory: { week: MD.MeasureWeek; value: string }[] = [];

            Object.entries(assessmentsData).forEach(([weekStr, weekDetails]: [string, RDPD.AssessmentDetails]) => {
                let measureWeek: MD.MeasureWeek = M.getMeasureWeek(weekStr);
                if (R.includeMeasureWeekInReport(reportWeek, measureWeek)) {
                    if (weekDetails.social && weekDetails.social.length > 0) {
                        let text = weekDetails.social.map(a => a.trim() + (a.trim().endsWith(".") ? "" : ".")).join("\r\n");
                        socialAssessmentsHistory.push({ week: measureWeek, value: text });
                    }
                }
            });

            mostRecentSocialAssessment = socialAssessmentsHistory.length > 0 ? socialAssessmentsHistory[socialAssessmentsHistory.length - 1].value : "";
        }
    }

    const genBehavioralAssessments = generatedData?.behavioralAssessments;
    if (genBehavioralAssessments) {
        setBehavioralAssessments(genBehavioralAssessments);
    }

    const genPhysicalAssessments = generatedData?.physicalAssessments;
    if (genPhysicalAssessments) {
        setPhysicalAssessments(genPhysicalAssessments);
    }

    const genSocialAssessments = generatedData?.socialAssessments;
    if (genSocialAssessments) {
        setSocialAssessments(genSocialAssessments);
        setSocialAssessmentsOriginal(genSocialAssessments);
    }
    else if (isGenerator && mostRecentSocialAssessment) {
        setSocialAssessments(mostRecentSocialAssessment);
        setSocialAssessmentsOriginal(mostRecentSocialAssessment);
    }

    const genContinuedAssessments = generatedData?.continuedNeedsAssessments;
    if (genContinuedAssessments) {
        setContinuedNeedsAssessments(genContinuedAssessments);
    }

    setIncludeBehavioralAssessments(generatedData?.inclusions?.behavioralAssessments ?? false);
    setIncludeContinuedNeedsAssessments(generatedData?.inclusions?.continuedNeedsAssessments ?? false);
    setIncludePhysicalAssessments(generatedData?.inclusions?.physicalAssessments ?? false);
    setIncludeSocialAssessments(generatedData?.inclusions?.socialAssessments ?? false);

    setAssessmentsGenerated(generatedData?.assessmentsGenerated ?? false);
};

interface processPlanParams{
    report: RD.EditableReport;
    // State Setters
    setPlan: Dispatch<SetStateAction<string>>;
    setPlanOriginal: Dispatch<SetStateAction<string>>;
    setPlanSettings: Dispatch<SetStateAction<RD.ReportGeneratedPlanSettings>>;
}

const processPlan = ({
    report,
    // State Setters
    setPlan,
    setPlanOriginal,
    setPlanSettings
}: processPlanParams): void => {

    const generatedData: RD.ReportGeneratedData = report.generated_data;

    const generatedPlanSettings = generatedData?.planSettings;
    if (generatedPlanSettings) {
        setPlanSettings(generatedPlanSettings);
    }

    const generatedPlan = generatedData?.plan;
    if (generatedPlan) {
        setPlan(generatedPlan);
        setPlanOriginal(generatedPlan);
    }
};

interface ProcessImmutableReportDataParams {
    isGenerator: boolean,
    notificationManager: NotificationManagerAPI,
    responseData: RD.ReportDataApiResponse,
    // State Setters
    setIsReportConfigurationDirty: Dispatch<SetStateAction<boolean>>,
    setBrand: Dispatch<SetStateAction<RD.BrandEnums>>,
    setClaimNumber: Dispatch<SetStateAction<string>>,
    setCoach: Dispatch<SetStateAction<string>>,
    setDateOfBirth: Dispatch<SetStateAction<string>>,
    setDateOfInjury: Dispatch<SetStateAction<string>>,
    setHasCoachingPlan: Dispatch<SetStateAction<boolean>>,
    setHasPhysicianOversight: Dispatch<SetStateAction<boolean>>,
    setPatientId: Dispatch<SetStateAction<number>>,
    setPatientName: Dispatch<SetStateAction<string>>,
    setPatientNameMasked: Dispatch<SetStateAction<string>>,
    setPayerType: Dispatch<SetStateAction<RD.PayerEnums>>,
    setPhysician: Dispatch<SetStateAction<string>>,
    setPreparedDate: Dispatch<SetStateAction<Date>>;
    setPreparerName: Dispatch<SetStateAction<string>>
    setProgramType: Dispatch<SetStateAction<string>>,
    setReport: Dispatch<SetStateAction<RD.EditableReport | undefined>>,
    setReportPlanWeeks: Dispatch<SetStateAction<number>>,
    setReportWeek: Dispatch<SetStateAction<RD.ReportWeek>>,
    setReportWeekOptions: Dispatch<SetStateAction<RD.ReportWeekOptions>>,
    setSamplePlanWeeks: Dispatch<SetStateAction<number>>,
    setCitations: Dispatch<SetStateAction<RD.Citations>>
}

export const processImmutableReportData = ({
    isGenerator,
    responseData,
    // State Setters
    setBrand,
    setClaimNumber,
    setCoach,
    setDateOfBirth,
    setDateOfInjury,
    notificationManager,
    setHasCoachingPlan,
    setHasPhysicianOversight,
    setIsReportConfigurationDirty,
    setPatientId,
    setPatientName,
    setPatientNameMasked,
    setPayerType,
    setPhysician,
    setPreparedDate,
    setPreparerName,
    setProgramType,
    setReport,
    setReportPlanWeeks,
    setReportWeek,
    setReportWeekOptions,
    setSamplePlanWeeks,
    setCitations
}: ProcessImmutableReportDataParams): [RD.EditableReport, RD.ReportWeek, number] | null => {

    if (!responseData) { notificationManager.addError("Error loading outcome report data. Contact a Karuna Admin."); return; }
    if (responseData.error) { notificationManager.addError(`Error loading outcome report data. Contact a Karuna Admin. : ${responseData.error}`); return; }

    let report: RD.EditableReport = responseData;

    if (!report.sample_data) {
        notificationManager.addError("Error loading Outcome Report data. Missing sample data. Contact a Karuna Admin.");
        return;
    }

    if (!isGenerator && !report.generated_data) {
        notificationManager.addError("Error loading Outcome Report data. Missing generated data. Contact a Karuna Admin.");
    }

    if (!processCoachingPlan({
        isGenerator,
        notificationManager,
        report,
        // State Setters
        setHasCoachingPlan,
        setProgramType
    })) {
        return;
    }

    const [reportWeek, reportPlanWeeks] = processReportConfiguration({
        isGenerator,
        report,
        // State Setters
        setIsReportConfigurationDirty,
        setPreparedDate,
        setPreparerName,
        setReportPlanWeeks,
        setReportWeek,
        setReportWeekOptions,
        setSamplePlanWeeks
    });

    // Validate that report has session data (defensive check - should be caught at creation)
    if (isGenerator && report.sample_data.sample_plan_weeks <= 0) {
        notificationManager.addError(
            "Cannot load report: No session data found for this patient. " +
            "The patient must have completed at least one coaching session with Practice Better notes."
        );
        return null;
    }

    processPatientInfo({
        report,
        // State Setters
        setBrand,
        setClaimNumber,
        setCoach,
        setDateOfBirth,
        setDateOfInjury,
        setHasPhysicianOversight,
        setPatientId,
        setPatientName,
        setPatientNameMasked,
        setPayerType,
        setPhysician
    });

    // Process citations
    const sampleData: RD.ReportSampleData = report.sample_data;
    if (sampleData.citations) {
        setCitations(sampleData.citations);
    }

    setReport(report);

    return [report, reportWeek, reportPlanWeeks];
}

interface ProcessMutableReportDataParams {
    isGenerator: boolean,
    report: RD.EditableReport,
    reportWeek: RD.ReportWeek,
    reportPlanWeeks: number,
    // State Setters
    setAllTrajectories: Dispatch<SetStateAction<T.Trajectories>>,
    setAssessmentsGenerated: Dispatch<SetStateAction<boolean>>,
    setAverageActivityTimePerWeek: Dispatch<SetStateAction<string>>,
    setBehavioralAssessments: Dispatch<SetStateAction<string>>,
    setContinuedNeedsAssessments: Dispatch<SetStateAction<string>>,
    setDiscoveries: Dispatch<SetStateAction<RD.Discoveries | null>>,
    setDiscoveriesOriginal: Dispatch<SetStateAction<RD.Discoveries | null>>,
    setEngagementLevel: Dispatch<SetStateAction<RD.GoalsProgress | null>>,
    setGoalsProgress: Dispatch<SetStateAction<RD.GoalsProgress | null>>,
    setGoals: Dispatch<SetStateAction<RD.Goals | null>>,
    setGoalsOriginal: Dispatch<SetStateAction<RD.Goals | null>>,
    setHistoryOfInjury: Dispatch<SetStateAction<string>>,
    setHistoryOfInjuryOriginal: Dispatch<SetStateAction<string>>,
    setHistoryOfInjuryCitation: Dispatch<SetStateAction<number | undefined>>,
    setIncludeAverageTime: Dispatch<SetStateAction<boolean>>,
    setIncludeBehavioralAssessments: Dispatch<SetStateAction<boolean>>,
    setIncludeContinuedNeedsAssessments: Dispatch<SetStateAction<boolean>>,
    setIncludeEngagementLevel: Dispatch<SetStateAction<boolean>>,
    setIncludeGoalsProgress: Dispatch<SetStateAction<boolean>>,
    setIncludeMedicationReduction: Dispatch<SetStateAction<boolean>>,
    setIncludePhysicalAssessments: Dispatch<SetStateAction<boolean>>,
    setIncludeReturnToWork: Dispatch<SetStateAction<boolean>>,
    setIncludeSessionsAttended: Dispatch<SetStateAction<boolean>>,
    setIncludeSocialAssessments: Dispatch<SetStateAction<boolean>>,
    setIncludeUniqueDays: Dispatch<SetStateAction<boolean>>,
    setIncludedTrajectories: Dispatch<SetStateAction<Record<string, boolean>>>,
    setMedicationReductionStatus: Dispatch<SetStateAction<RD.MedicationReductionStatus | null>>,
    setPhysicalAssessments: Dispatch<SetStateAction<string>>,
    setPlan: Dispatch<SetStateAction<string>>,
    setPlanOriginal: Dispatch<SetStateAction<string>>,
    setPlanSettings: Dispatch<SetStateAction<RD.ReportGeneratedPlanSettings>>,
    setReportPostProcessingTrigger: (updateFn: (prev: number) => number) => void,
    setReturnToWorkStatus: Dispatch<SetStateAction<RD.ReturnToWorkStatus | null>>,
    setSelectedImprovedTrajectoryTypes: Dispatch<SetStateAction<MD.MeasureType[]>>,
    setSessionsAttended: Dispatch<SetStateAction<number>>,
    setSocialAssessments: Dispatch<SetStateAction<string>>,
    setSocialAssessmentsOriginal: Dispatch<SetStateAction<string>>,
    setSubjectiveProgressSummary: Dispatch<SetStateAction<string>>,
    setSubjectiveProgressSummaryOriginal: Dispatch<SetStateAction<string>>,
    setSubjectiveProgressSummaryCitation: Dispatch<SetStateAction<number | undefined>>,
    setUniqueDays: Dispatch<SetStateAction<number>>
}

export const processMutableReportData = ({
    isGenerator,
    report,
    reportPlanWeeks,
    reportWeek,
    // State Setters
    setAllTrajectories,
    setAssessmentsGenerated,
    setAverageActivityTimePerWeek,
    setBehavioralAssessments,
    setContinuedNeedsAssessments,
    setDiscoveries,
    setDiscoveriesOriginal,
    setEngagementLevel,
    setGoalsProgress,
    setGoals,
    setGoalsOriginal,
    setHistoryOfInjury,
    setHistoryOfInjuryOriginal,
    setHistoryOfInjuryCitation,
    setIncludeAverageTime,
    setIncludeBehavioralAssessments,
    setIncludeContinuedNeedsAssessments,
    setIncludeEngagementLevel,
    setIncludeGoalsProgress,
    setIncludeMedicationReduction,
    setIncludePhysicalAssessments,
    setIncludeReturnToWork,
    setIncludeSessionsAttended,
    setIncludeSocialAssessments,
    setIncludeUniqueDays,
    setIncludedTrajectories,
    setMedicationReductionStatus,
    setPhysicalAssessments,
    setPlan,
    setPlanOriginal,
    setPlanSettings,
    setReportPostProcessingTrigger,
    setReturnToWorkStatus,
    setSelectedImprovedTrajectoryTypes,
    setSessionsAttended,
    setSocialAssessments,
    setSocialAssessmentsOriginal,
    setSubjectiveProgressSummary,
    setSubjectiveProgressSummaryOriginal,
    setSubjectiveProgressSummaryCitation,
    setUniqueDays
}: ProcessMutableReportDataParams) => {

    const historyOfInjuryCitations = processHistoryOfInjury({
        isGenerator,
        report,
        reportWeek,
        // State Setters
        setHistoryOfInjury,
        setHistoryOfInjuryOriginal
    });
    setHistoryOfInjuryCitation(historyOfInjuryCitations);

    processSubjectiveOutcomes({
        isGenerator,
        report,
        reportWeek,
        // State Setters
        setDiscoveries,
        setDiscoveriesOriginal,
        setEngagementLevel,
        setGoalsProgress,
        setGoals,
        setGoalsOriginal,
        setIncludeEngagementLevel,
        setIncludeGoalsProgress,
        setSubjectiveProgressSummary,
        setSubjectiveProgressSummaryOriginal,
        setSubjectiveProgressSummaryCitation
    });

    processObjectiveOutcomes({
        isGenerator,
        report,
        reportWeek,
        reportPlanWeeks,
        // State Setters
        setAllTrajectories,
        setAverageActivityTimePerWeek,
        setIncludeAverageTime,
        setIncludeMedicationReduction,
        setIncludeReturnToWork,
        setIncludeSessionsAttended,
        setIncludeUniqueDays,
        setIncludedTrajectories,
        setMedicationReductionStatus,
        setReturnToWorkStatus,
        setSelectedImprovedTrajectoryTypes,
        setSessionsAttended,
        setUniqueDays
    });

    processAssessments({
        isGenerator,
        report,
        reportWeek,
        // State Setters
        setAssessmentsGenerated,
        setBehavioralAssessments,
        setContinuedNeedsAssessments,
        setIncludeBehavioralAssessments,
        setIncludeContinuedNeedsAssessments,
        setIncludePhysicalAssessments,
        setIncludeSocialAssessments,
        setPhysicalAssessments,
        setSocialAssessments,
        setSocialAssessmentsOriginal
    });

    processPlan({
        report,
        // State Setters
        setPlan,
        setPlanOriginal,
        setPlanSettings
    });

    setReportPostProcessingTrigger(prev => ++prev);
};
